import { useUser } from '@auth-v2';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { Link, useNavigate } from 'react-router';

import { useActivity, useUserName } from '@queries';
import { activityIdAtom, activeUserIdAtom } from '@store';
import { Inline, u, ElvaLogoName, PropertyTrigger, BackArrowIcon, IconButton, Above, Text, ListIcon } from '@ui';

import * as css from './ActivityReportHeader.css';

type ActivityReportHeaderProps = {
    backLink: () => string;
    onToggleDrawer: () => void;
};

export function ActivityReportHeader({ backLink, onToggleDrawer }: ActivityReportHeaderProps) {
    const navigate = useNavigate();
    const { data: user } = useUser();
    const activityId = useAtomValue(activityIdAtom);
    const { data: activity } = useActivity(activityId);
    const activeUserId = useAtomValue(activeUserIdAtom);
    const { data: student } = useUserName(activeUserId);
    const coachView = user?.id !== activeUserId;
    const userLabel = coachView ? 'Student' : 'User';
    const name = coachView ? student : user?.user_metadata?.['name'];

    return (
        <Inline px={{ lg: u(6), xs: u(2) }} className={css.header}>
            <Above bp="lg">
                <div className={css.background} />
            </Above>
            <Inline flex={1} justifyContent="space-between" alignItems="center" className={css.headerContent}>
                <Inline alignItems="center" ml={u(1)}>
                    <Inline mr={u(3)} className={css.onlyUnderLg}>
                        <button onClick={onToggleDrawer}>
                            <ListIcon size={24} />
                        </button>
                    </Inline>

                    <Link to="/">
                        <ElvaLogoName width={120} />
                    </Link>
                </Inline>

                <Inline>
                    <MobileTags name={name} date={activity?.startTime} />
                    <Above bp="lg">
                        <Inline>
                            {name && <PropertyTrigger label={userLabel} values={[{ label: name }]} disabled />}
                            {activity?.startTime && (
                                <PropertyTrigger
                                    label="Date"
                                    values={[{ label: new Date(activity.startTime).toLocaleDateString() }]}
                                    disabled
                                />
                            )}

                            {!!activity?.tags?.length && (
                                <PropertyTrigger
                                    label="Tags"
                                    values={activity.tags.map((tag) => ({ label: tag.name })) ?? []}
                                    disabled
                                />
                            )}
                        </Inline>
                    </Above>
                </Inline>

                <IconButton onClick={() => navigate(backLink())}>
                    <BackArrowIcon />
                </IconButton>
            </Inline>
        </Inline>
    );
}

type MobileTagsProps = {
    name?: string;
    date?: string;
};

function MobileTags({ name, date }: MobileTagsProps) {
    return (
        <Inline className={css.onlyUnderLg}>
            {name && <Text fontWeight={600}>{name}</Text>}
            {name && date && <Text fontWeight={600}>·</Text>}
            {date && <Text fontWeight={600}>{format(new Date(date), 'MMM dd yyyy')}</Text>}
        </Inline>
    );
}
